import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import cuid from 'cuid';
import html2canvas from 'html2canvas';
import React, { useEffect, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { WildcardValidationCheckResponse } from '../utils/containsInvalidWildcards';

const CANVAS_WIDTH = 284;
const CANVAS_HEIGHT = 340;
const CANVAS_SCALE = 2.1;

type TemplatePreviewProps = {
  html: string;
  wildcardCheck?: WildcardValidationCheckResponse;
};

const TemplatePreview = ({ html, wildcardCheck }: TemplatePreviewProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(true);
  const scriptRegex = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  const doctypeRegex = /<!DOCTYPE[^>]*>/gi;
  const htmlRegex = /<html[^>]*>/gi;
  const htmlCloseRegex = /<\/html>/gi;
  const headRegex = /<head[^>]*>/gi;
  const headCloseRegex = /<\/head>/gi;
  const bodyRegex = /<body[^>]*>/gi;
  const bodyCloseRegex = /<\/body>/gi;
  const hrefRegex = /href="([^"]*)"/gi;

  const sanitizedHtml = html
    .replaceAll(scriptRegex, '')
    .replaceAll(doctypeRegex, '')
    .replaceAll(htmlRegex, '')
    .replaceAll(htmlCloseRegex, '')
    .replaceAll(headRegex, '')
    .replaceAll(headCloseRegex, '')
    .replaceAll(bodyRegex, '')
    .replaceAll(bodyCloseRegex, '')
    .replaceAll(hrefRegex, 'href=""');

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const htmlRenderRef = useRef<HTMLDivElement>(null);
  const htmlRenderId = useMemo(() => cuid(), []);

  useEffect(() => {
    if (canvasRef.current && htmlRenderRef.current) {
      setIsLoading(true);

      const scale = CANVAS_SCALE;
      const scaledWidth = CANVAS_WIDTH * scale;
      const scaledHeight = CANVAS_HEIGHT * scale;

      html2canvas(htmlRenderRef.current, {
        imageTimeout: 0,
        allowTaint: true,
        removeContainer: true,
        cache: undefined,
        scale: scale,
        width: scaledWidth,
        height: scaledHeight,
      })
        .then((capturedCanvas) => {
          // Adjust the canvas style to fit the div using CSS
          capturedCanvas.style.width = `${CANVAS_WIDTH}px`;
          capturedCanvas.style.height = `${CANVAS_HEIGHT}px`;

          if (canvasRef.current) {
            canvasRef.current.replaceWith(capturedCanvas);
          }

          setIsLoading(false);
        })
        .catch((error) => {
          console.error('html2canvas failed:', error);
          Sentry.captureException(`html2canvas failed: ${error}`);
          setIsLoading(false);
        });
    }
  }, [htmlRenderId, html, sanitizedHtml]);

  return (
    <Box position="relative" borderRadius="lg">
      {wildcardCheck?.invalid ? (
        <Alert
          status="warning"
          title="Content Error:"
          description={`${wildcardCheck.results[0].title} - ${wildcardCheck.results[0].description}`}
          hideCloseButton
        />
      ) : null}
      <canvas ref={canvasRef} height={CANVAS_HEIGHT} width={CANVAS_WIDTH} />
      {isLoading && (
        <Box position="absolute" top="40%" left="40%">
          <Spinner size="lg" />
        </Box>
      )}
      <Box
        ref={htmlRenderRef}
        id={htmlRenderId}
        position="absolute"
        top="-9999px"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    </Box>
  );
};

export default TemplatePreview;
